/** Synthetic compile-on-read notification IDs used by the Smart Action Center. */

const DYNAMIC_PREFIXES = [
  'order-pending-',
  'pending-order-', // legacy backend prefix
  'delivery-accepted-',
  'stock-low-',
  'low-stock-', // legacy backend prefix
  'shift-unassigned-',
  'worker-rate-missing-',
  'booking-unassigned-',
] as const

/** Platform inbox items service-core compiles on read (Parts B and C of packages). */
const DYNAMIC_IDS = new Set([
  'merchant-applications-pending',
  'offering-approvals-pending',
  'package-requests-pending',
])

export function isDynamicNotificationId(id: string | undefined | null): boolean {
  if (!id) return false
  if (DYNAMIC_IDS.has(id)) return true
  return DYNAMIC_PREFIXES.some((prefix) => id.startsWith(prefix))
}

export function isPendingOrderNotificationId(id: string | undefined | null): boolean {
  if (!id) return false
  return id.startsWith('order-pending-') || id.startsWith('pending-order-')
}

/** Prefer explicit `thing`; fall back to UUID suffix on order alert ids. */
export function orderIdFromNotification(notification: {
  id?: string | null
  thing?: string | null
}): string | null {
  const thing = notification.thing?.trim()
  if (thing) return thing
  const id = notification.id || ''
  const match = id.match(/^(?:order-pending-|pending-order-|delivery-accepted-)(.+)$/)
  return match?.[1] || null
}

export function mergeDismissedIds(current: string[], ids: string[]): string[] {
  const next = new Set(current)
  for (const id of ids) {
    if (id) next.add(id)
  }
  return Array.from(next)
}

export function persistDismissedIds(ids: string[]): void {
  localStorage.setItem('ch_dismissed_notifications', JSON.stringify(ids))
  window.dispatchEvent(new Event('storage'))
}

/** Only accept in-app paths so a stored absolute/external URL cannot escape the SPA. */
function internalAppPath(link?: string | null): string | null {
  if (!link) return null
  const trimmed = link.trim()
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return trimmed
  return null
}

/**
 * Deep-link for "Resolve Task". The API often sends a list page (`/orders/manage`)
 * without the entity id — that no-ops when the user is already on that page.
 */
export function resolveTaskPath(notification: {
  id?: string | null
  thing?: string | null
  workflowCode?: string | null
  link?: string | null
}): string | null {
  const code = notification.workflowCode?.toUpperCase() ?? ''
  if (code === 'PENDING_ORDER' || code === 'DELIVERY_ACCEPTED') {
    const orderId = orderIdFromNotification(notification)
    return orderId ? `/orders/manage?orderId=${encodeURIComponent(orderId)}` : '/orders/manage'
  }
  if (code === 'STOCK_LOW' || code === 'LOW_STOCK') {
    return '/stock'
  }
  if (code === 'SHIFT_UNASSIGNED') {
    return '/shifts'
  }
  if (code === 'BOOKING_UNASSIGNED') {
    return '/calendar'
  }
  if (code === 'MERCHANT_APPLICATION_PENDING') {
    return '/platform/companies'
  }
  return internalAppPath(notification.link)
}
